// AppRequest.js — "Request an app": which tools a clinician wants connected,
// asked in the panel (tap the kinds, optionally name them) and sent straight
// to HubSpot as the signed-in user, so the answer lands on their contact
// record (Apps wanted / Apps wanted details) without a trip to a web form.
import { useContext, useState } from 'react';
import { GlobalContext } from '../../api/GlobalContext';

// HubSpot form "Comprehend in-app: Request an app" (portal: Comprehend Health).
export const HS_PORTAL_ID = '50719340';
export const HS_APP_REQUEST_FORM_ID = '6580a696-a49e-48b9-bf3a-94bdd889c7cd';
const SUBMIT_URL = `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_APP_REQUEST_FORM_ID}`;

// Values are the Apps wanted property's internal option values.
export const APP_KINDS = [
  { value: 'RTM (remote therapeutic monitoring)', label: 'RTM' },
  { value: 'HEP (home exercise programs)', label: 'Home exercise (HEP)' },
  { value: 'Smart sensors / wearables', label: 'Sensors & wearables' },
  { value: 'Outcome measures', label: 'Outcome measures' },
  { value: 'Other', label: 'Other' },
];

/** The HubSpot Forms API body for one request. */
export function requestBody({ email, kinds, details, pageUri }) {
  const fields = [
    { objectTypeId: '0-1', name: 'email', value: email },
    { objectTypeId: '0-1', name: 'apps_wanted', value: kinds.join(';') },
  ];
  if (details) fields.push({ objectTypeId: '0-1', name: 'apps_wanted_details', value: details });
  return { fields, context: { pageUri, pageName: 'Comprehend: Apps tab' } };
}

export default function AppRequest({ intro }) {
  const { apiClient } = useContext(GlobalContext);
  const email = apiClient?.email || apiClient?.sessionEmail || '';
  const [kinds, setKinds] = useState([]);
  const [details, setDetails] = useState('');
  const [state, setState] = useState('idle'); // idle → sending → sent | failed

  const toggle = (value) => setKinds((list) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]));
  const ready = email && (kinds.length > 0 || details.trim()) && state !== 'sending';

  async function send() {
    setState('sending');
    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          requestBody({
            email,
            kinds: kinds.length ? kinds : ['Other'],
            details: details.trim(),
            pageUri: typeof window !== 'undefined' ? window.location.origin : '',
          }),
        ),
      });
      setState(response.ok ? 'sent' : 'failed');
    } catch (e) {
      setState('failed');
    }
  }

  if (state === 'sent') {
    return (
      <div className="d-flex align-items-center p-3 rounded" style={{ gap: 10, background: '#f0faf3', border: '1px solid #cfeedd' }} data-app-request-sent>
        <i className="fas fa-check-circle" style={{ color: '#2dce89', fontSize: 18 }} aria-hidden="true" />
        <span className="small" style={{ color: '#32325d' }}>
          Thanks! That goes straight to our team, and it shapes which apps we connect next.
        </span>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column" style={{ gap: 10 }} data-app-request>
      {intro}
      <div className="d-flex flex-wrap" style={{ gap: 6 }} role="group" aria-label="Kinds of apps">
        {APP_KINDS.map((kind) => {
          const on = kinds.includes(kind.value);
          return (
            <button
              key={kind.value}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(kind.value)}
              style={{
                padding: '4px 11px',
                borderRadius: 999,
                fontSize: 13,
                lineHeight: 1.4,
                cursor: 'pointer',
                border: `1px solid ${on ? '#5e72e4' : '#dee2e6'}`,
                background: on ? '#5e72e4' : '#fff',
                color: on ? '#fff' : '#525f7f',
                transition: 'background .1s, color .1s, border-color .1s',
              }}
            >
              {kind.label}
            </button>
          );
        })}
      </div>
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Which ones? e.g. MedBridge, Keet, Limber"
        value={details}
        maxLength={500}
        onChange={(e) => setDetails(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && ready && send()}
      />
      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <button type="button" className="btn btn-sm btn-primary" disabled={!ready} onClick={send}>
          {state === 'sending' ? 'Sending…' : 'Send request'}
        </button>
        {state === 'failed' && <small className="text-danger">Couldn't send that. Try again in a moment.</small>}
        {!email && <small className="text-muted">Sign in to send a request.</small>}
      </div>
    </div>
  );
}
